import { Table as TableType } from '@tanstack/react-table';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { useEffect, useMemo, useRef, useState } from 'react';

import {
  useGetSupplierAllTeam,
  useGetSupplierTeam,
} from '@/modules/workforce/Crew/apiHooks';
import { useSupplierTeamHook } from '@/modules/workforce/Crew/components/Crews/CrewDetails/Team/logic';
import PrintToolBar from '@/modules/workforce/Crew/components/Crews/printToolBar';
import { ISuppliers } from '@/modules/workforce/Crew/types';
import { Loading, Table } from '@/shared/components/atoms';
import { EmptyCard } from '@/shared/components/molecules';
import QrCodeModal from '@/shared/components/molecules/QrCodeModal.tsx';
import routes from '@/shared/constant/routes';
import { useAuth } from '@/shared/hooks/context/auth';

import FarmGirl from '~/images/shared/FarmGirl.svg';

const PER_PAGE = 10;

interface Props {
  status: 'ACTIVE' | 'INACTIVE';
  onePerPage: boolean;
  setOnePerPage: React.Dispatch<React.SetStateAction<boolean>>;
  qrcodeRef: React.MutableRefObject<null>;
  filter: ISuppliers.FilterProps<ISuppliers.IGetSupplierTeamMemberData>;
  setFilter: React.Dispatch<
    React.SetStateAction<
      ISuppliers.FilterProps<ISuppliers.IGetSupplierTeamMemberData>
    >
  >;
}

const Team: React.FC<Props> = ({
  status,
  setFilter,
  filter: { selectedRows, hasTeam },
  qrcodeRef,
  onePerPage,
  setOnePerPage,
}) => {
  const { user } = useAuth();

  const { t } = useTranslation('common');
  const { query, push } = useRouter();
  const supplierId = useMemo(() => query?.id as string, [query?.id]);
  const { supplierTeamTableColumns } = useSupplierTeamHook();

  const [pageNumber, setPageNumber] = useState<number>(1);
  const tableRef =
    useRef<TableType<ISuppliers.IGetSupplierTeamMemberData>>(null);
  const [selectAllPages, setSelectAllPages] = useState(false);

  const [openQrCodeModal, setOpenQrCodeModal] = useState(false);
  const [activeQrData, setActiveQrData] =
    useState<null | ISuppliers.IGetSupplierTeamMemberData>(null);

  const { data, isLoading } = useGetSupplierTeam({
    supplierId: supplierId,
    pageNumber,
    pageSize: PER_PAGE,
    workerName: undefined,
    status,
  });

  // GET all supplier pages when choose select all suppliers // all without pagination

  const { data: allTeamsData, isSuccess } = useGetSupplierAllTeam({
    trigger: selectAllPages,
    supplierId: supplierId,
  });

  const clearHandler = () => {
    tableRef.current?.resetRowSelection();
    setFilter((prev) => ({
      ...prev,
      allpagesData: [],
    }));
    // setSelectAllPages(false);
  };

  useEffect(() => {
    if (data && data.totalCount > 0)
      setFilter((prev) => ({ ...prev, hasTeam: false }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  // when select get all supplier in all pages set all suppliers returned data to be printed
  useEffect(() => {
    if (isSuccess && selectAllPages) {
      setFilter((prev) => ({
        ...prev,
        allpagesData: allTeamsData?.supplierTeam,
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSuccess, selectAllPages]);

  // when change selection and you were chosen select all then reset select all and all pages data
  useEffect(() => {
    if (selectAllPages) {
      setSelectAllPages(false);
      setFilter((prev) => ({
        ...prev,
        allpagesData: [],
      }));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRows]);

  const onPageChange = (page: number) => {
    setPageNumber(page);
  };

  // qr code methods
  const onClickQrHandler = (row: ISuppliers.IGetSupplierTeamMemberData) => {
    setActiveQrData(row);
    setOpenQrCodeModal(true);

    //
  };
  const closeQrModalHandler = () => {
    //
    setOpenQrCodeModal(false);
  };
  if (isLoading) {
    return (
      <div className='mt-100'>
        <Loading />
      </div>
    );
  }

  return (
    <div className='flex flex-col gap-16'>
      {/* TODO: move this to parent */}
      {!!selectedRows.length && (
        <PrintToolBar
          qrcodeRef={qrcodeRef}
          rowsLength={selectedRows.length}
          onClearTableSelection={clearHandler}
          isPrintDisabled={hasTeam}
          onePerPage={onePerPage}
          setOnePerPage={setOnePerPage}
        />
      )}

      <div>
        {data?.supplierTeam?.length ? (
          <Table<ISuppliers.IGetSupplierTeamMemberData>
            tableRef={tableRef}
            selection={status === 'INACTIVE' ? false : true}
            tableClassName='!border-spacing-y-[2px]'
            headCellClassName='ltr:text-left rtl:text-right text-surface-10'
            rowContainerClassName='cursor-pointer'
            tableWrapperClassName='border-xs !overflow-visible min-h-[50vh] border-neutralVariant-90 rounded-b-2xl'
            headContainerClassName='!bg-background-neutral-100'
            columns={supplierTeamTableColumns(
              t,
              onClickQrHandler,
              status,
              supplierId
            )}
            data={data?.supplierTeam || []}
            pagination={true}
            getIsSelectAll={(isSelectAll) =>
              setFilter((prev) => ({ ...prev, isSelectAll }))
            }
            getSelectedRows={(rows) => {
              setFilter((prev) => ({ ...prev, selectedRows: rows }));
            }}
            currentPage={pageNumber}
            perPage={PER_PAGE}
            totalCount={data?.totalCount || 0}
            onPageChange={onPageChange}
            onClickRow={(row) =>
              push(
                `${routes.suppliers}/${
                  query?.id
                }/worker/${row?.workerId?.toString()}`
              )
            }
          />
        ) : (
          <EmptyCard
            classeName='pb-16'
            message={t('no-workers-found')}
            vector={<FarmGirl />}
            variant='icon'
            fullSVG
          />
        )}
      </div>

      {/* TODO: refactor this */}
      {openQrCodeModal && (
        <QrCodeModal
          qrData={{
            qrCode: activeQrData?.qrCode,
            name: activeQrData?.name,
            logo: user?.companyLogo,
          }}
          open={openQrCodeModal}
          closeModal={closeQrModalHandler}
        />
      )}
    </div>
  );
};
export default Team;
